import {useNavigate, useParams} from "react-router-dom";
import React, {useEffect, useMemo, useRef, useState} from "react";

import {
    ArrowLeft,
    CalendarCheck,
    CalendarClock,
    CircleCheckBig,
    ClipboardList,
    Clock,
    File,
    FileText,
    MailMinus,
    MailPlus,
    MessageCircle,
    MinusCircle,
    MoreVertical, NotebookPen,
    RefreshCcw,
    Send,
} from "lucide-react";
import {SamTabedPage} from "../../../components/SamTabedPaged.tsx";
import Modal from "../../../components/Modal.tsx";
import {OrderDetailsTab} from "./OrderDetailsTab.tsx";
import {HistoryTab} from "./HistoryTab.tsx";
import {FilesTab} from "./FilesTab.tsx";
import {MessagesTab} from "./MessagesTab.tsx";
import {MultiFileUploadContentFromAppraiser} from "../../../components/MultiFileUploadFromAppraiser.tsx";
import {AddMessageForm} from "../../../components/AddMessageForm.tsx";
import {PrintButton} from "../../../components/PrintButton.tsx";
import {
    AvailableAction,
    DEFAULT_ORDER_DETAILS,
    OrderDetails,
} from "../../../api/ApiTypes.ts";
import {useOrderApi} from "../../../context/ApiContext.tsx";
import {MarkAsInspected} from "../../../components/MarkAsInspected.tsx";
import {ScheduleInspectionForm} from "../../../components/ScheduleInspectionForm.tsx";
import {RejectWithConditionsForm} from "../../../components/RejectWithConditionsForm.tsx";
import {RejectOrderForm} from "../../../components/RejectOrderForm.tsx";
import {AcceptOrderForm} from "../../../components/AcceptOrderForm.tsx";
import {ReAssignOrderForm} from "../../../components/ReassignOrderForm.tsx";
import {convertKebabToEnumAction} from "../../../utils/kebabhelper.ts";
import {AutoReplyYesForm} from "../../../components/AutoReplyYesForm.tsx";
import {AutoReplyNoForm} from "../../../components/AutoReplyNoForm.tsx";
import {OrderDetailsSkeleton} from "./OrderDetailsSkeleton.tsx";
import Tooltip from "../../../components/Tooltip.tsx";
import {useAuth} from "../../../context/AuthContext.tsx";
import {getReportTypeDisplayName, isUad36Order} from "./orderDetailsDisplay.ts";
import {Uad36Page} from "../../../components/Uad36Notice.tsx";

// Define modal configuration type
type ModalConfig = {
    title: string;
    component: React.ReactNode;
};

export const OrderDetailsLayout = () => {
    const navigate = useNavigate();
    const orderApi = useOrderApi();
    const { id, action } = useParams(); // Get the ID from URL parameters

    const [selectedTabIndex, setSelectedTabIndex] = useState(0); // Default to the first tab
    // State for dynamic modal
    const [isModalOpen, setModalOpen] = useState(false);
    const [orderDetailsLoading, setOrderDetailsLoading] = useState(false);
    const [myOrderDetails, setMyOrderDetails] = useState<OrderDetails>(DEFAULT_ORDER_DETAILS);
    const [urlParamsActionProcessed, setUrlParamsActionProcessed] = useState(false);

    const [modalConfig, setModalConfig] = useState<ModalConfig | null>(null);

    // Actions dropdown state
    const [isActionsDropdownOpen, setActionsDropdownOpen] = useState(false);
    const me = useAuth();
    const actionsDropdownRef = useRef<HTMLDivElement | null>(null);

    const refreshOrderDetails = async () => {
        if (!id) return;
        setOrderDetailsLoading(true);
        try {
            const details = await orderApi.getOrderDetails(id);
            setMyOrderDetails(details);
        }
        catch (error) {
            const wasInvited = await orderApi.wasInvited(id);
            if (wasInvited) {
                navigate("/appraiser/orderdetails/"+ id + "/no-longer-available");
            }
            else {
                navigate("/forbidden");
            }
            console.error("Failed to fetch order details:", error);
        }
        finally {
            setOrderDetailsLoading(false);
        }
    }

    const uad36Order = isUad36Order(myOrderDetails);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent): void {
            if (actionsDropdownRef.current &&
                !actionsDropdownRef.current.contains(event.target as Node)) {
                setActionsDropdownOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        refreshOrderDetails();
    }, []);

// effect to detect url params and either open a modal or open a tab
    useEffect(() => {
        // Only run when order is loaded and action hasn't been processed yet
        if (!orderDetailsLoading && myOrderDetails.id && action && !urlParamsActionProcessed) {
            //try modal actions first
            const actionToPerform = convertKebabToEnumAction(action);
            if (actionToPerform && myOrderDetails.availableActions &&
                myOrderDetails.availableActions.includes(actionToPerform)) {
                handleActionSelect(actionToPerform);
                setUrlParamsActionProcessed(true);
            }
            //no modal action found, try to open a tab
            else {
                const tabIndex = tabComponents.findIndex(tab => tab.slug === action.toLowerCase());
                if (tabIndex != -1) {
                    setSelectedTabIndex(tabIndex);
                }
                setUrlParamsActionProcessed(true);
            }
        }
    }, [myOrderDetails, orderDetailsLoading, action, id, urlParamsActionProcessed]);



    // Handle back navigation
    const handleBack = () => {
        navigate("/appraiser/dashboard");
    };

    // Modal handlers
    const closeModal = () => setModalOpen(false);

    // Handle successful action
    const handleActionSuccess = async ({navigateToTab, routeToDashboard} : {
        navigateToTab?: string;
        routeToDashboard?: boolean
    } = {}) => {
        closeModal();

        if (routeToDashboard) {
            handleBack();
            return;
        }

        await refreshOrderDetails();

        if (navigateToTab) {
            const tabIndex = tabComponents.findIndex(tab => tab.slug === navigateToTab.toLowerCase())
            if (tabIndex !== -1) {
                setSelectedTabIndex(tabIndex);
            }
        }
    };

    // Handle action selection from dropdown
    const handleActionSelect = (action: AvailableAction | string): void => {
        setActionsDropdownOpen(false);

        switch (action) {
            case AvailableAction.FILL_OUT_FORM:
                window.open(`/appraiser/orderdetails/${myOrderDetails.id}/fill-form`, "_blank");
                break;
            case AvailableAction.SCHEDULE_INSPECTION:
                let title = myOrderDetails.wasPreviouslyScheduled ? "Reschedule Inspection" : "Schedule Inspection";
                setModalConfig(
                    {
                        title: title,
                        component: (
                            <ScheduleInspectionForm
                                onSuccess={() => handleActionSuccess({navigateToTab: "histories"})}
                                onCancel={closeModal}
                                myOrderDetails={myOrderDetails || null}
                                inspectionActualDate={myOrderDetails.inspectionActualDate}
                                estimatedDeliveryDate={myOrderDetails.estimatedDeliveryDate}
                                inspectionDueDate={myOrderDetails.inspectionDueDate}
                                wasPreviouslyScheduled={myOrderDetails.wasPreviouslyScheduled}
                            />
                        ),
                    }
                );
                setModalOpen(true);
                break;
            case AvailableAction.MARK_INSPECTED:
                setModalConfig({
                    title: "Mark As Inspected",
                    component: (
                        <MarkAsInspected
                            onSuccess={() => handleActionSuccess({navigateToTab: "histories"})}
                            onCancel={closeModal}
                            orderId={myOrderDetails?.id || null}
                            inspectionActualDate={myOrderDetails.inspectionActualDate}
                            estimatedDeliveryDate={myOrderDetails.estimatedDeliveryDate}
                        >

                        </MarkAsInspected>
                    ),
                });
                setModalOpen(true);
                break;
            case AvailableAction.UPLOAD_FILES:
                setModalConfig({
                    title: "Upload Order Files",
                    component: (
                        <MultiFileUploadContentFromAppraiser
                            onSuccess={() => handleActionSuccess({navigateToTab: "files"})}
                            onCancel={closeModal}
                            forOrder={myOrderDetails?.id || null}
                            requiresEnv={myOrderDetails.requiresEnv}
                        />
                    ),
                });
                setModalOpen(true);
                break;

            case AvailableAction.SEND_MESSAGE: // Custom action not in enum
                setModalConfig({
                    title: "Add New Message",
                    component: (
                        <AddMessageForm
                            onSuccess={() => handleActionSuccess({navigateToTab: "messages"})}
                            onCancel={closeModal}
                            orderId={myOrderDetails?.id || null}
                        />
                    ),
                });
                setModalOpen(true);
                break;
            case AvailableAction.REJECT_WITH_CONDITIONS:
                setModalConfig({
                    title: "Reject With Conditions",
                    component: (
                        <RejectWithConditionsForm
                            orderId={myOrderDetails?.id}
                            onSuccess={handleActionSuccess}
                            onCancel={closeModal}
                        />
                    ),
                });
                setModalOpen(true);
                break;
            case AvailableAction.REJECT:
                setModalConfig({
                    title: "Reject",
                    component: (
                        <RejectOrderForm
                            orderId={myOrderDetails?.id}
                            onSuccess={() => handleActionSuccess({routeToDashboard: true})}
                            onCancel={closeModal}
                        />
                    ),
                })
                setModalOpen(true);
                break;
            case AvailableAction.REASSIGN:
                setModalConfig({
                    title: "Re-Assign Order",
                    component: (
                        <ReAssignOrderForm
                            orderId={myOrderDetails?.id}
                            onSuccess={handleActionSuccess}
                            onCancel={closeModal}
                        />
                    ),
                })
                setModalOpen(true);
                break;
            case AvailableAction.ACCEPT:
                setModalConfig({
                    title: "Accept Order",
                    component: (
                        <AcceptOrderForm
                            orderId={myOrderDetails?.id}
                            onSuccess={handleActionSuccess}
                            onCancel={closeModal}
                            orderDetails={myOrderDetails}
                        />
                    ),
                })
                setModalOpen(true);
                break;
            case AvailableAction.AUTO_REPLY_YES:
                setModalConfig({
                    title: "Order Is On Time",
                    component: (
                        <AutoReplyYesForm
                            orderId={myOrderDetails?.id}
                            onSuccess={handleActionSuccess}
                            onCancel={closeModal}
                        />
                    ),
                })
                setModalOpen(true);
                break;
            case AvailableAction.AUTO_REPLY_NO:
                setModalConfig({
                    title: "Order Is NOT On Time",
                    component: (
                        <AutoReplyNoForm
                            orderId={myOrderDetails?.id}
                            onSuccess={handleActionSuccess}
                            onCancel={closeModal}
                        />
                    ),
                })
                setModalOpen(true);
                break;
            default:
                console.log("Unhandled action:", action);
        }
    };

    // Toggle dropdown visibility
    const toggleActionsDropdown = () => {
        setActionsDropdownOpen(!isActionsDropdownOpen);
    };

    // Memoize the tab components so they don't rerender unless myOrderDetails changes
    const tabComponents = useMemo(() => {
        return [
            {
                name: 'Order Details',
                icon: <FileText className="h-5 w-5" />,
                slug: 'order-details',
                description: 'View order information and property details',
                component: () => <OrderDetailsTab orderDetails={myOrderDetails} />
            },
            {
                name: 'History & Revisions',
                slug: 'histories',
                icon: <Clock className="h-5 w-5" />,
                description: 'View order history and revisions',
                component: () => <HistoryTab orderId={myOrderDetails.id} histories={myOrderDetails?.histories} userType={me?.me?.userType} />
            },
            {
                name: 'Files',
                slug: 'files',
                icon: <ClipboardList className="h-5 w-5" />,
                description: 'Access and manage order files and documents',
                component: () => <FilesTab orderId={myOrderDetails.id} files={myOrderDetails?.files} userType={me?.me?.userType} />
            },
            {
                name: 'Messages',
                slug: 'messages',
                icon: <MessageCircle className="h-5 w-5" />,
                description: 'View and send messages related to this order',
                component: () => <MessagesTab orderId={myOrderDetails.id} messages={myOrderDetails?.messages} />
            }
        ];
    }, [myOrderDetails]); // Only recreate when myOrderDetails changes

    if (orderDetailsLoading) {
        return <OrderDetailsSkeleton/>
    }

    if (!myOrderDetails) {
        return (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mt-4">
                <h2 className="text-yellow-700 font-medium">Order Not Found</h2>
                <p className="text-yellow-600 mt-1"> The requested order details could not be found.</p>
                <button
                    onClick={handleBack}
                    className="mt-3 inline-flex items-center px-3 py-2 border border-yellow-300 text-sm leading-4 font-medium rounded-md text-yellow-700 bg-white hover:bg-yellow-50"
                >
                    Return to Orders
                </button>
            </div>
        );
    }

    // Action buttons with dropdown - Only show when there are available actions
    const hasAvailableActions = myOrderDetails.availableActions &&
        Array.isArray(myOrderDetails.availableActions) &&
        myOrderDetails.availableActions.length > 0;

    return (
        <Uad36Page uad36={uad36Order} label="UAD 3.6 Order">
            {/* Header with back button, title, and action buttons - Improved for mobile */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 space-y-4 sm:space-y-0">
                <div className="flex items-center">
                    <Tooltip text='Back'>
                        <button
                            onClick={handleBack}
                            className="mr-4 text-slate-600 hover:text-blue-600 transition-colors duration-200"
                            aria-label="Back to dashboard"
                        >
                            <ArrowLeft className="h-8 w-8 text-blue-700 hover:cursor-pointer" />
                        </button>
                    </Tooltip>
                    <div className="overflow-hidden">
                        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 truncate">
                            Order #{myOrderDetails.fileNumber}, {getReportTypeDisplayName(myOrderDetails.reportType1, myOrderDetails.deliveryStandard)}
                        </h1>
                        <p className="text-xs sm:text-sm text-slate-500 truncate">
                            Order ID: {myOrderDetails.id}
                        </p>
                    </div>
                </div>

                {/* Action buttons with dropdown - Mobile responsive */}
                <div className="flex flex-wrap gap-2 sm:space-x-3 justify-end">
                    {hasAvailableActions && (
                        <div className="relative" ref={actionsDropdownRef}>
                            <button
                                onClick={toggleActionsDropdown}
                                className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:cursor-pointer hover:bg-blue-700 shadow-sm"
                            >
                                <span className="hidden sm:inline">Actions</span>
                                <span className="sm:hidden">Actions</span>
                                <MoreVertical className="h-4 w-4 ml-1 sm:ml-2" />
                            </button>

                            {isActionsDropdownOpen && (
                                <div className="absolute right-0 mt-2 w-48 sm:w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                                    <div className="py-1" role="menu" aria-orientation="vertical">
                                        {myOrderDetails.availableActions.includes(AvailableAction.FILL_OUT_FORM) && (
                                            <button
                                                onClick={() => handleActionSelect(AvailableAction.FILL_OUT_FORM)}
                                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center"
                                                role="menuitem"
                                            >
                                                <NotebookPen className="h-4 w-4 mr-2" /> Complete Form
                                            </button>
                                        )}
                                        {myOrderDetails.availableActions.includes(AvailableAction.REASSIGN) && (
                                            <button
                                                onClick={() => handleActionSelect(AvailableAction.REASSIGN)}
                                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center hover:cursor-pointer"
                                                role="menuitem"
                                            >
                                                <CircleCheckBig className="h-4 w-4 mr-2" /> Re-Assign
                                            </button>
                                        )}

                                        {myOrderDetails.availableActions.includes(AvailableAction.SEND_MESSAGE) && (
                                            <button
                                                onClick={() => handleActionSelect(AvailableAction.SEND_MESSAGE)}
                                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center hover:cursor-pointer"
                                                role="menuitem"
                                            >
                                                <Send className="h-4 w-4 mr-2" /> Send Message
                                            </button>
                                        )}

                                        {myOrderDetails.availableActions.includes(AvailableAction.UPLOAD_FILES) && (
                                            <button
                                                onClick={() => handleActionSelect(AvailableAction.UPLOAD_FILES)}
                                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center hover:cursor-pointer"
                                                role="menuitem"
                                            >
                                                <File className="h-4 w-4 mr-2" /> Upload Files
                                            </button>
                                        )}

                                        {myOrderDetails.availableActions.includes(AvailableAction.SCHEDULE_INSPECTION) && (
                                            <button
                                                onClick={() => handleActionSelect(AvailableAction.SCHEDULE_INSPECTION)}
                                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center hover:cursor-pointer"
                                                role="menuitem"
                                            >
                                                <CalendarClock className="h-4 w-4 mr-2" /> Schedule Inspection
                                            </button>
                                        )}

                                        {myOrderDetails.availableActions.includes(AvailableAction.MARK_INSPECTED) && (
                                            <button
                                                onClick={() => handleActionSelect(AvailableAction.MARK_INSPECTED)}
                                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center hover:cursor-pointer"
                                                role="menuitem"
                                            >
                                                <CalendarCheck className="h-4 w-4 mr-2" /> Mark as Inspected
                                            </button>
                                        )}

                                        {myOrderDetails.availableActions.includes(AvailableAction.REJECT_WITH_CONDITIONS) && (
                                            <button
                                                onClick={() => handleActionSelect(AvailableAction.REJECT_WITH_CONDITIONS)}
                                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center hover:cursor-pointer"
                                                role="menuitem"
                                            >
                                               <RefreshCcw className="h-4 w-4 mr-2" /> Reject With Conditions
                                            </button>
                                        )}

                                        {myOrderDetails.availableActions.includes(AvailableAction.REJECT) && (
                                            <button
                                                onClick={() => handleActionSelect(AvailableAction.REJECT)}
                                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center hover:cursor-pointer"
                                                role="menuitem"
                                            >
                                                <MinusCircle className="h-4 w-4 mr-2" /> Reject
                                            </button>
                                        )}

                                        {myOrderDetails.availableActions.includes(AvailableAction.ACCEPT) && (
                                            <button
                                                onClick={() => handleActionSelect(AvailableAction.ACCEPT)}
                                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center hover:cursor-pointer"
                                                role="menuitem"
                                            >
                                                <CircleCheckBig className="h-4 w-4 mr-2" /> Accept
                                            </button>
                                        )}

                                        {myOrderDetails.availableActions.includes(AvailableAction.AUTO_REPLY_YES) && (
                                            <button
                                                onClick={() => handleActionSelect(AvailableAction.AUTO_REPLY_YES)}
                                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center hover:cursor-pointer"
                                                role="menuitem"
                                            >
                                                <MailPlus className="h-4 w-4 mr-2" /> Reply Order On Time
                                            </button>
                                        )}

                                        {myOrderDetails.availableActions.includes(AvailableAction.AUTO_REPLY_NO) && (
                                            <button
                                                onClick={() => handleActionSelect(AvailableAction.AUTO_REPLY_NO)}
                                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center hover:cursor-pointer"
                                                role="menuitem"
                                            >
                                                <MailMinus className="h-4 w-4 mr-2" /> Reply Order NOT On Time
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <PrintButton orderId={myOrderDetails.id}/>
                </div>
            </div>

            {/* Tabs layout with memoized tabs */}
            <SamTabedPage
                tabs={tabComponents}
                selectedTabIndex={selectedTabIndex}
                setSelectedTabIndex={setSelectedTabIndex}
            />

            {/* Dynamic Modal */}
            {modalConfig && (
                <Modal
                    title={modalConfig.title}
                    isOpen={isModalOpen}
                    onClose={closeModal}
                    maxWidth={"2xl"}
                >
                    {modalConfig.component}
                </Modal>
            )}
        </Uad36Page>
    );
};
